export type CreativeMediaKind = "audio" | "video";

export interface CreativeFileInspection {
  valid: boolean;
  duration: number;
  message?: string;
}

const EXTENSIONS: Record<CreativeMediaKind, ReadonlySet<string>> = {
  audio: new Set(["aac", "flac", "m4a", "mp3", "ogg", "opus", "wav", "webm"]),
  video: new Set(["avi", "m4v", "mkv", "mov", "mp4", "mpeg", "mpg", "ogv", "webm"]),
};

export function fileLooksLikeCreativeKind(
  file: Pick<File, "name" | "type">,
  kind: CreativeMediaKind,
) {
  const mime = file.type.trim().toLowerCase();
  if (mime) return mime.startsWith(`${kind}/`);
  const extension = file.name.split(".").pop()?.trim().toLowerCase() ?? "";
  return EXTENSIONS[kind].has(extension);
}

export async function inspectCreativeFile(
  file: File,
  kind: CreativeMediaKind,
): Promise<CreativeFileInspection> {
  const label = kind === "audio" ? "áudio" : "vídeo";
  if (!fileLooksLikeCreativeKind(file, kind)) {
    return { valid: false, duration: 0, message: `Selecione um arquivo de ${label} válido.` };
  }

  const objectURL = URL.createObjectURL(file);
  const media = document.createElement(kind);
  media.preload = "metadata";

  try {
    const duration = await new Promise<number>((resolve, reject) => {
      let timeout = 0;
      const cleanup = () => {
        window.clearTimeout(timeout);
        media.removeEventListener("loadedmetadata", onLoaded);
        media.removeEventListener("error", onError);
      };
      const onLoaded = () => {
        cleanup();
        if (kind === "video") {
          const video = media as HTMLVideoElement;
          if (video.videoWidth <= 0 || video.videoHeight <= 0) {
            reject(new Error("video stream missing"));
            return;
          }
        }
        resolve(media.duration);
      };
      const onError = () => {
        cleanup();
        reject(new Error("media decode failed"));
      };
      timeout = window.setTimeout(() => {
        cleanup();
        reject(new Error("metadata timeout"));
      }, 10000);
      media.addEventListener("loadedmetadata", onLoaded);
      media.addEventListener("error", onError);
      media.src = objectURL;
    });

    if (!Number.isFinite(duration) || duration <= 0) {
      return { valid: false, duration: 0, message: `Não foi possível ler este ${label}.` };
    }
    return { valid: true, duration };
  } catch {
    return {
      valid: false,
      duration: 0,
      message: `O arquivo selecionado não contém um ${label} reproduzível.`,
    };
  } finally {
    media.removeAttribute("src");
    media.load();
    URL.revokeObjectURL(objectURL);
  }
}
